import { Avatar } from 'primereact/avatar';
import dateFormat from '../../../../utils/date-format';
import React, { useState, useEffect } from 'react';
import { UserSummaryType } from '../../../../api/types/user/user-summary.type';
import { UserType } from '../../../../api/types/user/user-type';
import { Image } from 'primereact/image';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { ScrollPanel } from 'primereact/scrollpanel';
import {
  Gender,
  MaritalStatus,
} from '../../../../api/types/user/user-summary.type';
import { toast } from 'react-toastify';
import { isValidEmail } from '../../../../utils/utils-generat';
import { updateUserDetailsOperation } from './operations/update-user-details-operation';
import { ChangePasswordFlow } from './components/password-change/change-password-flow';
import { PhoneNumberComponent } from '../../../../utils/international-to-israeli';
import { logoutOperation } from './operations/logout-operation';

interface UserPageProfileModel {
  user: UserSummaryType;
  displayChangePassword: boolean;
}

interface UserFormData {
  firstName: string;
  lastName: string;
  nationalId: string;
  dateOfBirth: Date | null;
  gender: Gender | null;
  maritalStatus: MaritalStatus | null;
  phoneNumber: string;
  email: string;
  city: string;
  street: string;
  houseNumber: string;
  flatNumber: string;
  postcode: string;
  immigrateCountry: string | null;
  immigrateDate: Date | null;
}

interface FieldChange {
  field: keyof UserFormData;
  originalValue: any;
  newValue: any;
}

export const UserPageProfile: React.FC<UserPageProfileModel> = ({
  user,
  displayChangePassword = false,
}) => {
  // Helper to convert international phone format to Israeli display format (054-1233333)
  const convertInternationalToIsraeliDisplay = (
    phoneNumber: string,
  ): string => {
    if (!phoneNumber) return '';

    const cleaned = phoneNumber.replace(/[^\d+]/g, '');

    if (cleaned.startsWith('+972')) {
      const digits = cleaned.substring(4);
      if (digits.length >= 2) {
        const formatted = '0' + digits.substring(0, 10);
        if (formatted.length > 3) {
          return formatted.substring(0, 3) + '-' + formatted.substring(3);
        }
        return formatted;
      }
    }

    if (cleaned.startsWith('0')) {
      const digits = cleaned.substring(0, 10);
      if (digits.length > 3) {
        return digits.substring(0, 3) + '-' + digits.substring(3);
      }
      return digits;
    }

    if (cleaned.startsWith('972')) {
      const digits = cleaned.substring(3);
      if (digits.length >= 2) {
        const formatted = '0' + digits.substring(0, 10);
        if (formatted.length > 3) {
          return formatted.substring(0, 3) + '-' + formatted.substring(3);
        }
        return formatted;
      }
    }

    return phoneNumber;
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<UserFormData>({
    firstName: user.firstName,
    lastName: user.lastName,
    nationalId: user.nationalId,
    dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth) : null,
    gender: user.gender,
    maritalStatus: user.maritalStatus,
    phoneNumber: convertInternationalToIsraeliDisplay(user.phoneNumber),
    email: user.email,
    city: user.city || '',
    street: user.street || '',
    houseNumber: user.houseNumber || '',
    flatNumber: user.flatNumber || '',
    postcode: user.postcode || '',
    immigrateCountry: user.immigrateCountry,
    immigrateDate: user.immigrateDate ? new Date(user.immigrateDate) : null,
  });

  const [originalData] = useState<UserFormData>({ ...formData });
  const [fieldChanges, setFieldChanges] = useState<FieldChange[]>([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<keyof UserFormData, string>>
  >({});

  useEffect(() => {
    const changes: FieldChange[] = [];
    Object.keys(formData).forEach((key) => {
      const field = key as keyof UserFormData;
      if (
        JSON.stringify(formData[field]) !== JSON.stringify(originalData[field])
      ) {
        changes.push({
          field,
          originalValue: originalData[field],
          newValue: formData[field],
        });
      }
    });
    setFieldChanges(changes);
  }, [formData, originalData]);

  const isFieldChanged = (field: keyof UserFormData): boolean => {
    return fieldChanges.some((change) => change.field === field);
  };

  const getModifiedFields = (): Partial<UserFormData> => {
    const modifiedFields: Partial<UserFormData> = {};
    fieldChanges.forEach((change) => {
      modifiedFields[change.field] = change.newValue;
    });
    return modifiedFields;
  };

  const resetField = (field: keyof UserFormData) => {
    setFormData((prev) => ({
      ...prev,
      [field]: originalData[field],
    }));
  };

  const genderOptions = [
    { label: 'זכר', value: Gender.Male },
    { label: 'נקבה', value: Gender.Female },
  ];

  const maritalStatusOptions = [
    { label: 'רווק/ה', value: MaritalStatus.Single },
    { label: 'נשוי/ה', value: MaritalStatus.Married },
    { label: 'גרוש/ה', value: MaritalStatus.Divorced },
    { label: 'אלמן/ה', value: MaritalStatus.Widowed },
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserFormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'שם פרטי הוא שדה חובה';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'שם משפחה הוא שדה חובה';
    }
    if (!formData.nationalId.trim()) {
      newErrors.nationalId = 'תעודת זהות היא שדה חובה';
    }
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'תאריך לידה הוא שדה חובה';
    }
    if (!formData.gender) {
      newErrors.gender = 'מגדר הוא שדה חובה';
    }
    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'מצב משפחתי הוא שדה חובה';
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'מספר טלפון הוא שדה חובה';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'כתובת אימייל היא שדה חובה';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'כתובת אימייל לא תקינה';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof UserFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const emptyToNull = (value: string): string | null => {
    return value.trim() === '' ? null : value;
  };

  const handleUserDataSubmit = async () => {
    await updateUserDetailsOperation(
      user.id,
      formData.firstName,
      formData.lastName,
      formData.nationalId,
      formData.phoneNumber,
      formData.phoneNumber,
      formData.email,
      formData.dateOfBirth,
      emptyToNull(formData.city),
      emptyToNull(formData.street),
      emptyToNull(formData.houseNumber),
      emptyToNull(formData.flatNumber),
      emptyToNull(formData.postcode),
      formData.gender,
      formData.immigrateCountry,
      formData.immigrateDate,
      formData.maritalStatus,
    );
  };

  const updatePhoneNumber = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    let formatted = '';

    if (digitsOnly.length > 0) {
      if (!digitsOnly.startsWith('05') && digitsOnly.length >= 2) {
        formatted = '05' + digitsOnly.substring(0, 8);
      } else {
        formatted = digitsOnly.substring(0, 10);
      }

      if (formatted.length > 3) {
        formatted = formatted.substring(0, 3) + '-' + formatted.substring(3);
      }
    }
    handleInputChange('phoneNumber', formatted);
  };

  const confirmSubmit = async () => {
    try {
      const modifiedFields = getModifiedFields();
      toast.success('הפרטים עודכנו בהצלחה');
      setIsEditMode(false);
      setShowConfirmationDialog(false);
    } catch (error) {
      toast.error('שגיאה בעדכון הפרטים');
      console.error('Error updating user data:', error);
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setErrors({});
    setIsEditMode(false);
  };

  const inputClassName = (field: keyof UserFormData) =>
    `upp-input${errors[field] ? ' upp-invalid' : ''}`;

  const roleLabel =
    user?.role === UserType.ADMINISTRATION
      ? 'מנהלה'
      : user?.role === UserType.LECTURER
        ? 'מרצה'
        : 'סטודנט';

  const renderField = (
    field: keyof UserFormData,
    label: string,
    editor: React.ReactNode,
    viewer: React.ReactNode,
  ) => {
    const isChanged = isFieldChanged(field);
    return (
      <div style={{ maxWidth: '300px' }} className="upp-field" key={field}>
        <div className="upp-field-label">
          <span>{label}</span>
          {isChanged && (
            <span className="upp-field-label-actions">
              <span className="upp-change-dot" />
              <Button
                icon="pi pi-undo"
                className="p-button-text p-button-sm upp-reset-btn"
                onClick={() => resetField(field)}
                tooltip="איפוס לשינוי הקודם"
                tooltipOptions={{ position: 'left' }}
              />
            </span>
          )}
        </div>
        {isEditMode ? editor : <div className="upp-value">{viewer}</div>}
      </div>
    );
  };

  const emptyValue = (text = 'לא ידוע') => (
    <span className="upp-value-empty">{text}</span>
  );

  const confirmationDialogFooter = (
    <div>
      <Button
        label="ביטול"
        icon="pi pi-times"
        onClick={() => setShowConfirmationDialog(false)}
        className="p-button-text"
      />
      <Button
        label="אישור"
        icon="pi pi-check"
        onClick={confirmSubmit}
        className="p-button-success"
        autoFocus
      />
    </div>
  );

  const handleLogoutConfirm = async () => {
    await logoutOperation(setLogoutLoading);
  };

  const logoutDialogFooter = (
    <div>
      <Button
        label="ביטול"
        icon="pi pi-times"
        onClick={() => setShowLogoutDialog(false)}
        className="p-button-text"
        disabled={logoutLoading}
      />
      <Button
        label="אישור"
        icon="pi pi-check"
        onClick={handleLogoutConfirm}
        className="p-button-danger"
        loading={logoutLoading}
        disabled={logoutLoading}
        autoFocus
      />
    </div>
  );

  return (
    <div className="upp-root">
      <style>{`
        .upp-root {
          width: 100%;
          padding: 20px 24px;
          background: #ffffff;
          border-radius: 10px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          box-sizing: border-box;
          color: #212529;
        }
        .upp-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding-bottom: 14px;
          margin-bottom: 6px;
          border-bottom: 1px solid #eef0f3;
        }
        .upp-header-identity {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }
        .upp-header-avatar { flex-shrink: 0; }
        .upp-header-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }
        .upp-header-name {
          margin: 0;
          font-size: 1.0625rem;
          font-weight: 600;
          color: #111827;
          line-height: 1.3;
        }
        .upp-header-role {
          margin: 0;
          color: #6b7280;
          font-size: 0.8125rem;
        }
        .upp-header-name-edit {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .upp-header-name-edit input { max-width: 170px; }
        .upp-header-actions {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .upp-changes-indicator {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: #eff6ff;
          color: #1d4ed8;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 500;
          border: 1px solid #dbeafe;
          margin-inline-end: 4px;
        }
        .upp-section { padding: 14px 0 4px; }
        .upp-section + .upp-section { border-top: 1px solid #f1f3f5; }
        .upp-section-title {
          margin: 0 0 12px 0;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #4b5563;
          letter-spacing: 0.02em;
        }
        .upp-section-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px 18px;
        }
        @media (max-width: 1100px) {
          .upp-section-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 900px) {
          .upp-section-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 560px) {
          .upp-section-grid { grid-template-columns: 1fr; }
        }
        .upp-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
          min-width: 0;
        }
        .upp-field-label {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
          min-height: 18px;
        }
        .upp-field-label-actions {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .upp-change-dot {
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: #3b82f6;
          display: inline-block;
        }
        .upp-reset-btn.p-button {
          padding: 0 !important;
          width: 1.25rem !important;
          height: 1.25rem !important;
          color: #6b7280 !important;
        }
        .upp-reset-btn .pi { font-size: 0.7rem !important; }
        .upp-value {
          font-size: 0.875rem;
          color: #111827;
          padding: 5px 0 7px;
          min-height: 28px;
          border-bottom: 1px solid #eef0f3;
          line-height: 1.4;
          word-break: break-word;
        }
        .upp-value-empty { color: #9ca3af; }
        .upp-input {
          width: 100%;
          padding: 7px 10px;
          border-radius: 6px;
          border: 1px solid #d1d5db;
          font-size: 0.875rem;
          height: 34px;
          outline: none;
          font-family: inherit;
          transition: border-color 0.15s, box-shadow 0.15s;
          box-sizing: border-box;
          background: #ffffff;
          color: #111827;
        }
        .upp-input:hover:not(:disabled) { border-color: #9ca3af; }
        .upp-input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
        }
        .upp-input.upp-invalid { border-color: #dc3545; }
        .upp-input.upp-invalid:focus {
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
        }
        .upp-section .p-calendar { width: 100%; }
        .upp-section .p-calendar .p-inputtext,
        .upp-section .p-dropdown {
          height: 34px;
          font-size: 0.875rem;
          border-radius: 6px;
          border: 1px solid #d1d5db;
        }
        .upp-section .p-calendar .p-inputtext { padding: 0 10px; }
        .upp-section .p-dropdown .p-dropdown-label {
          padding: 0 10px;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
        }
        .upp-section .p-calendar.p-invalid .p-inputtext,
        .upp-section .p-dropdown.p-invalid { border-color: #dc3545; }
        .upp-footer-actions {
          display: flex;
          justify-content: center;
          gap: 12px;
          padding-top: 18px;
          margin-top: 18px;
          border-top: 1px solid #eef0f3;
        }
      `}</style>

      <div className="upp-header">
        <div className="upp-header-identity">
          <div className="upp-header-avatar">
            {user.fileUrl ? (
              <Image
                src={user.fileUrl}
                alt={`${user.firstName} ${user.lastName}`}
                width="64"
                preview
                pt={{
                  image: {
                    style: {
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                    },
                  },
                }}
              />
            ) : (
              <Avatar
                label={
                  (user.firstName?.charAt(0) || '') +
                  (user.lastName?.charAt(0) || '')
                }
                shape="circle"
                style={{
                  width: '64px',
                  height: '64px',
                  fontSize: '1.25rem',
                  backgroundColor: '#eef2ff',
                  color: '#4338ca',
                  fontWeight: 600,
                }}
              />
            )}
          </div>
          <div className="upp-header-info">
            {!isEditMode ? (
              <>
                <h2 className="upp-header-name">
                  {user.firstName} {user.lastName}
                </h2>
                <p className="upp-header-role">{roleLabel}</p>
              </>
            ) : (
              <>
                <div className="upp-header-name-edit">
                  <input
                    type="text"
                    value={formData.firstName}
                    placeholder="שם פרטי"
                    onChange={(e) =>
                      handleInputChange('firstName', e.target.value)
                    }
                    className={inputClassName('firstName')}
                  />
                  <input
                    type="text"
                    value={formData.lastName}
                    placeholder="שם משפחה"
                    onChange={(e) =>
                      handleInputChange('lastName', e.target.value)
                    }
                    className={inputClassName('lastName')}
                  />
                </div>
                <p className="upp-header-role">{roleLabel}</p>
              </>
            )}
          </div>
        </div>
        <div className="upp-header-actions">
          {isEditMode && fieldChanges.length > 0 && (
            <span className="upp-changes-indicator">
              <span className="upp-change-dot" />
              {fieldChanges.length} שדות עודכנו
            </span>
          )}
          {!isEditMode ? (
            <Button
              icon="pi pi-pencil"
              label="עריכה"
              onClick={() => setIsEditMode(true)}
              className="p-button-text p-button-sm"
            />
          ) : (
            <>
              <Button
                icon="pi pi-times"
                label="ביטול"
                onClick={handleCancel}
                className="p-button-text p-button-sm"
              />
              <Button
                icon="pi pi-check"
                label="שמירה"
                onClick={handleUserDataSubmit}
                className="p-button-success p-button-sm"
              />
            </>
          )}
        </div>
      </div>

      <div className="upp-section">
        <h3 className="upp-section-title">פרטים אישיים</h3>
        <div style={{ maxWidth: '1000px' }} className="upp-section-grid">
          {renderField(
            'nationalId',
            'תעודת זהות',
            <input
              type="text"
              value={formData.nationalId}
              onChange={(e) => handleInputChange('nationalId', e.target.value)}
              className={inputClassName('nationalId')}
            />,
            user.nationalId || emptyValue(),
          )}
          {renderField(
            'dateOfBirth',
            'תאריך לידה',
            <Calendar
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e.value)}
              dateFormat="dd/mm/yy"
              className={errors.dateOfBirth ? 'p-invalid' : ''}
              showIcon={false}
            />,
            user.dateOfBirth
              ? dateFormat.displayFormatDate(user.dateOfBirth)
              : emptyValue(),
          )}
          {renderField(
            'gender',
            'מגדר',
            <Dropdown
              value={formData.gender}
              options={genderOptions}
              onChange={(e) => handleInputChange('gender', e.value)}
              className={errors.gender ? 'p-invalid' : ''}
              style={{ width: '100%' }}
            />,
            user.gender === Gender.Male
              ? 'זכר'
              : user.gender === Gender.Female
                ? 'נקבה'
                : emptyValue(),
          )}
          {renderField(
            'maritalStatus',
            'מצב משפחתי',
            <Dropdown
              value={formData.maritalStatus}
              options={maritalStatusOptions}
              onChange={(e) => handleInputChange('maritalStatus', e.value)}
              className={errors.maritalStatus ? 'p-invalid' : ''}
              style={{ width: '100%' }}
            />,
            user.maritalStatus === MaritalStatus.Single
              ? 'רווק/ה'
              : user.maritalStatus === MaritalStatus.Married
                ? 'נשוי/ה'
                : user.maritalStatus === MaritalStatus.Divorced
                  ? 'גרוש/ה'
                  : user.maritalStatus === MaritalStatus.Widowed
                    ? 'אלמן/ה'
                    : emptyValue(),
          )}
        </div>
      </div>

      <div style={{ maxWidth: '1000px' }} className="upp-section">
        <h3 className="upp-section-title">פרטי התקשרות</h3>
        <div className="upp-section-grid">
          {renderField(
            'phoneNumber',
            'טלפון',
            <input
              type="text"
              value={formData.phoneNumber}
              maxLength={11}
              onChange={(e) => updatePhoneNumber(e.target.value)}
              className={inputClassName('phoneNumber')}
              dir="ltr"
            />,
            user.phoneNumber ? (
              <PhoneNumberComponent phoneNumber={user.phoneNumber} />
            ) : (
              emptyValue()
            ),
          )}
          {renderField(
            'email',
            'דוא"ל',
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={inputClassName('email')}
              dir="ltr"
            />,
            user.email || emptyValue(),
          )}
        </div>
      </div>

      <div style={{ maxWidth: '1000px' }} className="upp-section">
        <h3 className="upp-section-title">כתובת</h3>
        <div className="upp-section-grid">
          {renderField(
            'city',
            'עיר',
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className={inputClassName('city')}
            />,
            user.city || emptyValue(),
          )}
          {renderField(
            'street',
            'רחוב',
            <input
              type="text"
              value={formData.street}
              onChange={(e) => handleInputChange('street', e.target.value)}
              className={inputClassName('street')}
            />,
            user.street || emptyValue(),
          )}
          {renderField(
            'houseNumber',
            'מספר בית',
            <input
              type="text"
              value={formData.houseNumber}
              onChange={(e) => handleInputChange('houseNumber', e.target.value)}
              className={inputClassName('houseNumber')}
            />,
            user.houseNumber || emptyValue(),
          )}
          {renderField(
            'flatNumber',
            'מספר דירה',
            <input
              type="text"
              value={formData.flatNumber}
              onChange={(e) => handleInputChange('flatNumber', e.target.value)}
              className={inputClassName('flatNumber')}
            />,
            user.flatNumber || emptyValue(),
          )}
          {renderField(
            'postcode',
            'מיקוד',
            <input
              type="text"
              value={formData.postcode}
              onChange={(e) => handleInputChange('postcode', e.target.value)}
              className={inputClassName('postcode')}
            />,
            user.postcode || emptyValue(),
          )}
        </div>
      </div>

      <div style={{ maxWidth: '1000px' }} className="upp-section">
        <h3 className="upp-section-title">פרטי עלייה</h3>
        <div className="upp-section-grid">
          {renderField(
            'immigrateCountry',
            'ארץ עלייה',
            <input
              type="text"
              value={formData.immigrateCountry || ''}
              onChange={(e) =>
                handleInputChange('immigrateCountry', e.target.value)
              }
              className={inputClassName('immigrateCountry')}
            />,
            user.immigrateCountry || emptyValue('לא זמין'),
          )}
          {renderField(
            'immigrateDate',
            'תאריך עלייה',
            <Calendar
              value={formData.immigrateDate}
              onChange={(e) => handleInputChange('immigrateDate', e.value)}
              dateFormat="dd/mm/yy"
              className={errors.immigrateDate ? 'p-invalid' : ''}
              showIcon={false}
            />,
            user.immigrateDate
              ? dateFormat.displayFormatDate(user.immigrateDate)
              : emptyValue('לא זמין'),
          )}
        </div>
      </div>

      {displayChangePassword && (
        <div className="upp-footer-actions">
          <ChangePasswordFlow
            userPhoneNumber={user.phoneNumber}
            userEmail={user.email}
          />
          <Button
            label="התנתקות"
            icon="pi pi-sign-out"
            onClick={() => setShowLogoutDialog(true)}
            className="p-button-danger p-button-outlined p-button-sm"
          />
        </div>
      )}

      <Dialog
        header="אישור עדכון פרטים"
        visible={showConfirmationDialog}
        style={{ width: '450px' }}
        footer={confirmationDialogFooter}
        onHide={() => setShowConfirmationDialog(false)}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>האם אתה בטוח שברצונך לעדכן את השדות הבאים?</p>
        </div>
        <ScrollPanel style={{ height: '200px' }}>
          <div style={{ padding: '8px' }}>
            {fieldChanges.map((change, index) => (
              <div
                key={index}
                style={{
                  padding: '8px',
                  marginBottom: '8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                }}
              >
                <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                  {getFieldLabel(change.field)}
                </div>
                <div style={{ display: 'flex', gap: '8px', color: '#6c757d' }}>
                  <span>מערך:</span>
                  <span>{formatValue(change.originalValue)}</span>
                  <span>→</span>
                  <span>{formatValue(change.newValue)}</span>
                </div>
              </div>
            ))}
          </div>
        </ScrollPanel>
      </Dialog>

      <Dialog
        header="אישור התנתקות"
        visible={showLogoutDialog}
        style={{ width: '450px' }}
        footer={logoutDialogFooter}
        onHide={() => !logoutLoading && setShowLogoutDialog(false)}
        closable={!logoutLoading}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '16px 0',
          }}
        >
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: '2rem', color: '#f59e0b' }}
          />
          <p style={{ margin: 0, fontSize: '1rem', color: '#495057' }}>
            שים לב, הינך עומד לבצע התנתקות מהמערכת, האם אתה בטוח?
          </p>
        </div>
      </Dialog>
    </div>
  );
};

const getFieldLabel = (field: keyof UserFormData): string => {
  const labels: Record<keyof UserFormData, string> = {
    firstName: 'שם פרטי',
    lastName: 'שם משפחה',
    nationalId: 'תעודת זהות',
    dateOfBirth: 'תאריך לידה',
    gender: 'מגדר',
    maritalStatus: 'מצב משפחתי',
    phoneNumber: 'טלפון',
    email: 'דוא"ל',
    city: 'עיר',
    street: 'רחוב',
    houseNumber: 'מספר בית',
    flatNumber: 'מספר דירה',
    postcode: 'מיקוד',
    immigrateCountry: 'ארץ עלייה',
    immigrateDate: 'תאריך עלייה',
  };
  return labels[field];
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return 'לא זמין';
  if (value instanceof Date) return dateFormat.displayFormatDate(value);
  return String(value);
};
